import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogIn, LogOut, Menu, X, Calendar, Phone, Video, Eye, FileText, Plus, Link } from "lucide-react";
import useAuth from "@/hooks/stores/useAuth";
import { authService } from "@/services/authService";
import useFormSidebar from "@/hooks/stores/useFormSidebar";
import useInterviewerDashboard from "@/hooks/stores/useInterviewerDashboard";
import useUserDashboard from "@/hooks/stores/useUserDashboard";
import logo from "@/assets/images/logo.png";

export function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, clearTokens } = useAuth();
  const { toggleSidebar } = useFormSidebar();
  const { activeTab: interviewerTab, setActiveTab: setInterviewerTab } = useInterviewerDashboard();
  const { activeTab: userTab, setActiveTab: setUserTab } = useUserDashboard();
  const { userFlow } = useAuth();
  
  // Check if we're on specific pages
  const isFormPage = location.pathname === '/user-form';
  const isInterviewerDashboard = location.pathname === '/interviewer-dashboard';
  const isUserDashboard = location.pathname === '/user-dashboard';

  // Disabled scroll effect - navbar stays static
  // useEffect(() => {
  //   let ticking = false;
    
  //   const handleScroll = () => {
  //     if (!ticking) {
  //       requestAnimationFrame(() => {
  //         const scrollTop = window.scrollY;
          
  //         // Different thresholds for scrolling down vs up
  //         const shouldBeScrolled = isScrolled 
  //           ? scrollTop > 20  // When scrolled, switch back to full width when very close to top
  //           : scrollTop > 100; // When not scrolled, switch to narrow width after scrolling more
          
  //         if (shouldBeScrolled !== isScrolled) {
  //           setIsScrolled(shouldBeScrolled);
  //         }
  //         ticking = false;
  //       });
  //       ticking = true;
  //     }
  //   };

  //   window.addEventListener('scroll', handleScroll, { passive: true });
  //   return () => window.removeEventListener('scroll', handleScroll);
  // }, [isScrolled]);

  const handleLogin = () => {
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      // Use authService.logout() which properly clears React Query cache
      await authService.logout();
      // Always navigate to home after logout
      navigate('/', { replace: true });
      setIsMobileMenuOpen(false);
    } catch (error) {
      // Even if logout API fails, still navigate to home
      console.error('Logout error:', error);
      navigate('/', { replace: true });
      setIsMobileMenuOpen(false);
    }
  };

  const handleHome = () => {
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    if (isFormPage) {
      // On form pages, toggle the sidebar instead
      toggleSidebar();
    } else {
      // On other pages, toggle the regular mobile menu
    setIsMobileMenuOpen(!isMobileMenuOpen);
    }
  };

  const handleTabClick = (tab) => {
    if (isInterviewerDashboard) {
      setInterviewerTab(tab);
    } else if (isUserDashboard) {
      setUserTab(tab);
    }
    setIsMobileMenuOpen(false);
  };

  const interviewerTabs = [
    { id: 'slots', label: 'Sessions', icon: Calendar },
    { id: 'remote-calls', label: 'Remote Calls', icon: Phone },
    { id: 'upload', label: 'Upload Videos', icon: Video },
    { id: 'library', label: 'Video Library', icon: Eye },
  ];

  const getUserTabs = () => {
    const baseTabs = [
      { 
        id: 'booking', 
        label: userFlow === 'remote' ? 'Session Details' : 'Booking Details', 
        icon: Calendar 
      }
    ];

    if (userFlow === 'remote') {
      baseTabs.push({ id: 'meeting', label: 'Meeting Link', icon: Link });
    } else {
      baseTabs.push({ id: 'videos', label: 'My Videos', icon: Video });
    }

    baseTabs.push(
      { id: 'form', label: 'My Form', icon: FileText },
      { id: 'invite', label: 'Invite Friends', icon: Plus }
    );

    return baseTabs;
  };

  return (
    <>
      <nav 
        className="fixed top-0 left-0 right-0 z-50"
      >
        <div className="pt-0 px-0">
          <div className="bg-[#F9F4E5] border-b border-[#F5F1E6] w-full rounded-none">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-[4.5rem] md:h-[4.7rem]">
                {/* Logo Section */}
                <div 
                  className="flex items-center cursor-pointer group transition-transform duration-300 hover:scale-105" 
                  onClick={handleHome}
                >
                    <img 
                    src={logo}
                      alt="Postscrypt" 
                    className="h-6 md:h-7"
                    />
                </div>
                
                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center space-x-4">
                  {isAuthenticated ? (
                    <button 
                      onClick={handleLogout}
                      className="px-4 py-2 bg-primary text-white rounded-lg border border-[#F5F1E6] flex items-center group transition-all duration-300 hover:scale-105"
                    >
                      <LogOut className="mr-2 transition-transform duration-300 group-hover:-translate-x-1" size={16} />
                      <span>Sign Out</span>
                    </button>
                  ) : (
                    <button 
                      onClick={handleLogin}
                      className="px-4 py-2 bg-primary border border-primary-border text-white rounded-lg flex items-center group transition-all duration-300 hover:scale-105"
                    >
                      <LogIn className="mr-2 transition-transform duration-300 group-hover:translate-x-1" size={16} />
                      <span>Sign In</span>
                    </button>
                  )}
                </div>

                {/* Mobile Menu Button */}
                <div className="md:hidden">
                  <button 
                    onClick={toggleMobileMenu}
                    className="p-2 rounded-lg transition-all duration-300 text-foreground hover:bg-[#F5F1E6]"
                  >
                    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Professional Mobile Menu Overlay */}
      {isMobileMenuOpen && !isFormPage && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop with smooth fade */}
          <div 
            className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/40 backdrop-blur-md mobile-menu-backdrop"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
          
          {/* Main Menu Container */}
          <div className="absolute top-[4.5rem] left-0 right-0 bottom-0 flex flex-col mobile-menu-content">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-[#F9F4E5] to-[#F5F1E6] border-b border-secondary-border/30 backdrop-blur-xl">
              <div className="px-6 py-4">
                <h3 className="text-lg font-semibold text-foreground">
                  {(isInterviewerDashboard || isUserDashboard) ? 'Dashboard' : 'Menu'}
                </h3>
              </div>
            </div>

            {/* Content Section */}
            <div className="flex-1 bg-gradient-to-b from-background/95 to-background/90 backdrop-blur-xl overflow-y-auto">
              <div className="px-6 py-6 space-y-6">
                
                {/* Dashboard Tabs */}
                {(isInterviewerDashboard || isUserDashboard) && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      {(isInterviewerDashboard ? interviewerTabs : getUserTabs()).map((tab) => {
                        const IconComponent = tab.icon;
                        const currentTab = isInterviewerDashboard ? interviewerTab : userTab;
                        const isActive = currentTab === tab.id;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => handleTabClick(tab.id)}
                            className={`w-full flex items-center px-4 py-4 rounded-xl text-left mobile-menu-button group ${
                              isActive
                                ? 'bg-primary text-white shadow-lg shadow-primary/25 scale-[1.02]'
                                : 'bg-secondary/50 border border-secondary-border/50 hover:bg-secondary hover:shadow-md hover:scale-[1.01] text-foreground'
                            }`}
                          >
                            <div className={`p-2 rounded-lg mr-4 transition-all duration-300 ${
                              isActive 
                                ? 'bg-white/20 text-white' 
                                : 'bg-primary/10 text-primary group-hover:bg-primary/20'
                            }`}>
                              <IconComponent size={18} />
                            </div>
                            <div className="flex-1">
                              <div className={`font-medium ${isActive ? 'text-white' : 'text-foreground'}`}>
                                {tab.label}
                              </div>
                            </div>
                            {isActive && (
                              <div className="w-2 h-2 bg-white rounded-full ml-2"></div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Divider */}
                {(isInterviewerDashboard || isUserDashboard) && (
                  <div className="border-t border-secondary-border/30 pt-6">
                  </div>
                )}

                {/* Authentication Section */}
                <div className="space-y-3">
              {isAuthenticated ? (
                <button 
                  onClick={handleLogout}
                      className="w-full flex items-center px-4 py-4 bg-red-50 border border-red-200 text-red-700 rounded-xl transition-all duration-300 hover:bg-red-100 hover:shadow-md hover:scale-[1.01] group"
                    >
                      <div className="p-2 bg-red-100 rounded-lg mr-4 transition-all duration-300 group-hover:bg-red-200">
                        <LogOut size={18} />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium">Sign Out</div>
                        <div className="text-xs text-red-600">End your session</div>
                      </div>
                </button>
              ) : (
                <button 
                  onClick={handleLogin}
                      className="w-full flex items-center px-4 py-4 bg-primary text-white rounded-xl transition-all duration-300 hover:bg-primary/90 hover:shadow-lg hover:scale-[1.01] group"
                    >
                      <div className="p-2 bg-white/20 rounded-lg mr-4 transition-all duration-300 group-hover:bg-white/30">
                        <LogIn size={18} />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium">Sign In</div>
                        <div className="text-xs text-white/80">Access your account</div>
                      </div>
                </button>
              )}
                </div>
              </div>
            </div>

            {/* Footer Section */}
            <div className="bg-gradient-to-r from-secondary/80 to-secondary/60 border-t border-secondary-border/30 backdrop-blur-xl">
              <div className="px-6 py-4 text-center">
                <p className="text-xs text-muted-foreground">
                  © 2025 Postscrypt. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
} 